// @flow
import type {Element} from 'react'
import React from 'react'

import util from 'utils/MiscUtils'
import useEnterOverride from 'utils/hooks/useEnterOverride'
import {Checkbox} from 'components/proposals/Part/ui'
import Value from 'components/proposals/Part/ui/Value'
import RoleName from 'components/proposals/Part/shared/Editor/RoleName'
import RolePurpose from 'components/proposals/Part/shared/Editor/RolePurpose'
import Accountabilities from 'components/proposals/Part/shared/Editor/Accountabilities'
import Domains from 'components/proposals/Part/shared/Editor/Domains'
import {destroySummary} from 'components/proposals/Part/LinkedRole/summaryText'
import type {PickAlert, UpdateCallback, DeleteCallback} from 'components/proposals/types'
import type {
  Accountability,
  Domain,
  Circle,
  LinkedRole,
  Organization,
} from 'components/proposals/Part/LinkedRole/types'

type Props = $ReadOnly<{
  accountabilities: Accountability[],
  changeKey: string,
  circle: Circle,
  domains: Domain[],
  organization: Organization,
  destroy: boolean,
  isReadonly: boolean,
  linkedRole: LinkedRole,
  onChange: UpdateCallback,
  onDelete: DeleteCallback,
  pickAlert: PickAlert,
}>

const buildValue = (value) => new Value(value, value)

function UpdateLinkedRoleEditor({
  accountabilities,
  changeKey,
  circle,
  domains,
  organization,
  destroy,
  isReadonly,
  linkedRole,
  onChange,
  onDelete,
  pickAlert,
}: Props): null | Element<"div"> {
  const [enterOverride, changeEnterOverride] = useEnterOverride()
  const {originalRole} = linkedRole

  if (!originalRole) {
    util.error('An original role must exist in a linked role:', linkedRole)
    return null
  }

  return (
    <div>
      <RoleName
        disabled
        id={`EditLinkedRole-${changeKey}-name`}
        name={buildValue(originalRole.localizedName)}
        onChange={onChange}
        organization={organization}
        pickAlert={pickAlert}
        strikethrough={destroy}
      />
      <RolePurpose
        disabled
        id={`EditLinkedRole-${changeKey}-purpose`}
        label={organization.purposeLabel}
        onChange={onChange}
        organization={organization}
        pickAlert={pickAlert}
        purpose={buildValue(originalRole.purpose)}
        strikethrough={destroy}
      />
      <Domains
        changeEnterOverride={changeEnterOverride}
        circle={circle}
        domains={domains}
        enterOverride={enterOverride}
        label={organization.domainsLabel}
        isReadonly={isReadonly}
        isRemovingRoleOrCircle={destroy}
        onChange={onChange}
        onDelete={onDelete}
        organization={organization}
        pickAlert={pickAlert}
      />
      <Accountabilities
        accountabilities={accountabilities}
        changeEnterOverride={changeEnterOverride}
        circle={circle}
        enterOverride={enterOverride}
        isReadonly={isReadonly}
        isRemovingRoleOrCircle={destroy}
        onChange={onChange}
        onDelete={onDelete}
        organization={organization}
        pickAlert={pickAlert}
      />
      <hr />
      <div test-id="remove-linked-role-checkbox">
        <Checkbox
          checked={destroy}
          disabled={isReadonly}
          label={I18n.t('proposals.link_role.remove_link', {role: linkedRole.localizedName})}
          onChange={onChange}
          path={['destroy']}
        />
        {destroy && (
          <span>
            {destroySummary(linkedRole)}
          </span>
        )}
      </div>
    </div>
  )
}

export default UpdateLinkedRoleEditor
